import { createFileRoute, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { uploadLookImageAtKey } from "@/lib/storage";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchCreatorForUser,
  fetchCreatorProducts,
  fetchCreatorCollections,
  searchCatalog,
  reorderCreatorProducts,
  fetchCollection,
  setCollectionProducts,
  updateCollection,
  type Product,
  type CatalogFilters,
} from "@/lib/queries";
import { resolveImage } from "@/lib/image-map";
import {
  TrendingUp, Eye, MousePointerClick, PoundSterling, Plus, Search,
  X, ChevronUp, ChevronDown, Trash2, Edit3, ArrowLeft,
  Link2, Loader2, Check,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useServerFn } from "@tanstack/react-start";
import { listMyPartnerships, updatePartnershipStatus, type Partnership } from "@/lib/partnerships.functions";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { LooksManager } from "@/components/looks-manager";
import { SocialVerification } from "@/components/social-verification";
import { VerifiedBadge } from "@/components/verified-badge";
import { TwoFactorSettings } from "@/components/two-factor-settings";
import { AuthMethodBadge } from "@/components/auth-method-badge";
import { CopyButton as SharedCopyButton } from "@/components/copy-button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CodeOfConductModal, COC_VERSION } from "@/components/code-of-conduct";
import {
  ProductSkeleton,
  CollectionSkeleton,
  StatSkeleton,
  ListItemSkeleton,
  ProfileSkeleton,
  TableRowSkeleton,
  CollectionEditorSkeleton,
} from "@/components/skeleton-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/creator-dashboard")({
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const { data: u, error: userError } = await supabase.auth.getUser();
    if (userError || !u.user) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    const { data: rolesRows, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", u.user.id);
    if (error) {
      console.error("[creator-dashboard gate] role check failed", error);
      throw redirect({ to: "/" });
    }
    const roles = (rolesRows ?? []).map((r) => r.role as string);
    if (roles.includes("admin")) throw redirect({ to: "/admin" });
    if (!roles.includes("creator")) throw redirect({ to: "/apply" });
  },
  head: () => ({
    meta: [
      { title: "Creator Dashboard — Pluggz" },
      { name: "description", content: "Your private Pluggz workspace: curate products, build collections and looks, and track commissions." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Dashboard,
});

const tabs = ["Overview", "Curate", "Collections", "Looks", "Profile", "Payouts", "Smart Links", "Partnerships", "Account"] as const;
type Tab = (typeof tabs)[number];

function WelcomeBanner({
  creator,
  onGoToProfile,
}: {
  creator: { id: string; name: string | null; bio: string | null; created_at?: string };
  onGoToProfile: () => void;
}) {
  const storageKey = `pluggz.welcome-dismissed-${creator.id}`;
  const [dismissed, setDismissed] = useState(true);
  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  const createdAt = creator.created_at ? new Date(creator.created_at).getTime() : 0;
  const isFresh = Date.now() - createdAt < 5 * 60 * 1000;
  const name = (creator.name ?? "").trim().toLowerCase();
  const nameIsDefault = name === "" || name === "new creator" || name.startsWith("creator");
  const bioEmpty = !creator.bio || creator.bio.trim() === "";

  if (dismissed || !isFresh || !nameIsDefault || !bioEmpty) return null;

  const dismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div className="mt-6 flex items-start justify-between gap-4 border border-accent/40 bg-accent/10 p-5">
      <div>
        <p className="font-serif text-lg">Welcome to Pluggz!</p>
        <p className="mt-1 text-sm text-muted-foreground">Complete your profile to go live.</p>
        <button
          type="button"
          onClick={onGoToProfile}
          className="mt-3 inline-block bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background transition hover:opacity-90"
        >
          Complete profile
        </button>
      </div>
      <button
        type="button"
        aria-label="Dismiss welcome banner"
        onClick={dismiss}
        className="text-muted-foreground transition hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

type OnboardingCreator = {
  id: string;
  handle: string;
  avatar_url: string | null;
  bio: string | null;
  status: string;
  onboarding_dismissed?: boolean;
};

function OnboardingChecklist({
  creator,
  onNavigate,
}: {
  creator: OnboardingCreator;
  onNavigate: (tab: Tab) => void;
}) {
  const qc = useQueryClient();
  const shareKey = `pluggz.onboarding-shared-${creator.id}`;
  const [sharedDone, setSharedDone] = useState(false);
  const [autoDismissed, setAutoDismissed] = useState(false);

  useEffect(() => {
    try {
      setSharedDone(localStorage.getItem(shareKey) === "1");
    } catch {}
  }, [shareKey]);

  const counts = useQuery({
    queryKey: ["onboarding-counts", creator.id],
    queryFn: async () => {
      const [{ count: products }, { count: collections }] = await Promise.all([
        supabase
          .from("creator_products")
          .select("*", { count: "exact", head: true })
          .eq("creator_id", creator.id),
        supabase
          .from("collections")
          .select("*", { count: "exact", head: true })
          .eq("creator_id", creator.id),
      ]);
      return { products: products ?? 0, collections: collections ?? 0 };
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("creators")
        .update({ onboarding_dismissed: true })
        .eq("id", creator.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-creator"] });
    },
  });

  const items = [
    {
      key: "photo",
      label: "Add a profile photo",
      done: !!creator.avatar_url,
      action: () => onNavigate("Profile"),
    },
    {
      key: "bio",
      label: "Write your bio",
      done: !!creator.bio && creator.bio.trim().length > 20,
      action: () => onNavigate("Profile"),
    },
    {
      key: "products",
      label: "Add 5 products to your storefront",
      done: (counts.data?.products ?? 0) >= 5,
      action: () => onNavigate("Curate"),
    },
    {
      key: "collection",
      label: "Create your first collection",
      done: (counts.data?.collections ?? 0) >= 1,
      action: () => onNavigate("Collections"),
    },
    {
      key: "share",
      label: "Share your storefront link",
      done: sharedDone,
      action: async () => {
        const url =
          typeof window !== "undefined"
            ? `${window.location.origin}/creator/${creator.handle}`
            : `/creator/${creator.handle}`;
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Storefront link copied");
        } catch {
          toast.error("Couldn't copy — copy it manually: " + url);
        }
        try { localStorage.setItem(shareKey, "1"); } catch {}
        setSharedDone(true);
      },
    },
  ];

  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  const allDone = completed === total;

  useEffect(() => {
    if (!allDone || autoDismissed || creator.onboarding_dismissed) return;
    const t = setTimeout(() => {
      setAutoDismissed(true);
      dismissMutation.mutate();
    }, 3000);
    return () => clearTimeout(t);
  }, [allDone, autoDismissed, creator.onboarding_dismissed]);

  if (creator.onboarding_dismissed) return null;
  if (autoDismissed) return null;
  if (completed === total && !allDone) return null;

  return (
    <div className="mt-6 border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              {allDone ? "Setup complete" : `${completed} of ${total} complete`}
            </p>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-foreground transition-all"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
        </div>
        {!allDone && (
          <button
            type="button"
            aria-label="Dismiss onboarding checklist"
            onClick={() => dismissMutation.mutate()}
            className="text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {allDone ? (
        <div className="mt-6 text-center">
          <p className="font-serif text-xl">🎉 You're all set!</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your storefront is ready.
          </p>
        </div>
      ) : (
        <ul className="mt-5 divide-y divide-border">
          {items.map((item) => (
            <li key={item.key} className="flex items-center gap-3 py-3">
              <span
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
                  item.done
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-border"
                }`}
                aria-hidden
              >
                {item.done && <Check className="h-3 w-3" />}
              </span>
              <span
                className={`flex-1 text-sm ${item.done ? "text-muted-foreground line-through" : ""}`}
              >
                {item.label}
              </span>
              {!item.done && (
                <button
                  type="button"
                  onClick={item.action}
                  className="text-xs uppercase tracking-widest text-foreground hover:opacity-70"
                >
                  Do this →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("Overview");
  const qc = useQueryClient();
  const { data: creator } = useQuery({
    queryKey: ["my-creator", user?.id],
    queryFn: () => fetchCreatorForUser(user!.id),
    enabled: !!user,
  });

  if (!creator) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <p className="editorial-eyebrow">Studio</p>
        <h1 className="mt-2 font-serif text-4xl">No storefront yet</h1>
        <p className="mt-4 text-muted-foreground">Apply to open a curated storefront on Pluggz.</p>
        <Link to="/apply" className="mt-8 inline-block bg-foreground px-6 py-3 text-xs uppercase tracking-widest text-background">Apply as a creator</Link>
      </div>
    );
  }

  const cocVersion = (creator as { coc_version?: number | null }).coc_version ?? 0;
  const cocAcceptedAt = (creator as { coc_accepted_at?: string | null }).coc_accepted_at ?? null;
  const needsCoc = !cocAcceptedAt || cocVersion < COC_VERSION;

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      {needsCoc && (
        <CodeOfConductModal
          creatorId={creator.id}
          onAccepted={() => qc.invalidateQueries({ queryKey: ["my-creator", user?.id] })}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="editorial-eyebrow">Studio</p>
          <h1 className="mt-2 font-serif text-4xl">Welcome back, {creator.name.split(" ")[0]}</h1>
        </div>
        <div className="flex items-center gap-3">
          <img src={resolveImage(creator.avatar_url)} alt={creator.name} className="h-10 w-10 rounded-full object-cover" />
          <div className="text-right text-xs">
            <p className="font-medium">@{creator.handle}</p>
            <StatusPill status={creator.status as "pending" | "approved" | "live" | "rejected"} />
          </div>
        </div>
      </div>

      <StatusBanner status={creator.status as "pending" | "approved" | "live" | "rejected"} handle={creator.handle} />

      <WelcomeBanner creator={creator} onGoToProfile={() => setTab("Profile")} />

      <OnboardingChecklist creator={creator} onNavigate={setTab} />

      <div className="mt-10 overflow-x-auto border-b border-border [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <nav aria-label="Dashboard sections" role="tablist" className="flex gap-1 whitespace-nowrap">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} role="tab" aria-selected={tab === t} className={`flex-shrink-0 px-4 py-3 text-sm tracking-wide transition ${tab === t ? "border-b-2 border-foreground -mb-px" : "text-muted-foreground hover:text-foreground"}`}>
              {t}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-10">
        {tab === "Overview" && <Overview creatorId={creator.id} />}
        {tab === "Curate" && <Curate creatorId={creator.id} />}
        {tab === "Collections" && <Collections creatorId={creator.id} />}
        {tab === "Looks" && <LooksManager creatorId={creator.id} />}
        {tab === "Profile" && <ProfileEditor creatorId={creator.id} />}
        {tab === "Payouts" && <Payouts creatorId={creator.id} />}
        {tab === "Smart Links" && <SmartLinks handle={creator.handle} creatorId={creator.id} />}
        {tab === "Partnerships" && <Partnerships />}
        {tab === "Account" && <AccountSettings creatorId={creator.id} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, delta }: { icon: typeof Eye; label: string; value: string; delta: string }) {
  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-3 font-serif text-3xl">{value}</p>
      <p className="mt-1 text-xs text-accent">{delta}</p>
    </div>
  );
}

type CreatorStatus = "pending" | "approved" | "live" | "rejected";

function StatusPill({ status }: { status: CreatorStatus }) {
  const tone: Record<CreatorStatus, string> = {
    pending: "bg-amber-100 text-amber-900",
    approved: "bg-emerald-100 text-emerald-900",
    live: "bg-emerald-700 text-white",
    rejected: "bg-rose-100 text-rose-900",
  };
  return (
    <span className={`mt-1 inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone[status]}`}>
      {status}
    </span>
  );
}

function StatusBanner({ status, handle }: { status: CreatorStatus; handle: string }) {
  if (status === "live") return null;
  const copy: Record<Exclude<CreatorStatus, "live">, { title: string; body: string; tone: string }> = {
    pending: {
      title: "Application under review",
      body: "You can keep curating products and building looks. Your storefront will go public once an admin approves and takes it live.",
      tone: "border-amber-300 bg-amber-50 text-amber-900",
    },
    approved: {
      title: "Approved — almost live",
      body: "An admin will take your storefront live shortly. Keep polishing your profile in the meantime.",
      tone: "border-emerald-300 bg-emerald-50 text-emerald-900",
    },
    rejected: {
      title: "Application not accepted",
      body: "Your storefront is not public. Reach out to support if you'd like another review.",
      tone: "border-rose-300 bg-rose-50 text-rose-900",
    },
  };
  const c = copy[status];
  return (
    <div className={`mt-6 border px-4 py-3 text-sm ${c.tone}`}>
      <p className="font-medium">{c.title}</p>
      <p className="mt-1 opacity-80">{c.body}</p>
      {status === "approved" && (
        <p className="mt-1 text-xs opacity-70">Preview: /creator/{handle}</p>
      )}
    </div>
  );
}

function Overview({ creatorId }: { creatorId: string }) {
  const sevenDaysAgo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString();
  }, []);
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString();
  }, []);

  const stats = useQuery({
    queryKey: ["creator-overview", creatorId, sevenDaysAgo],
    queryFn: async () => {
      const [clicks7d, clicks30d, commissions, topProducts] = await Promise.all([
        supabase.from("clicks").select("id", { count: "exact", head: true }).eq("creator_id", creatorId).gte("created_at", sevenDaysAgo),
        supabase.from("clicks").select("id", { count: "exact", head: true }).eq("creator_id", creatorId).gte("created_at", thirtyDaysAgo),
        supabase.from("commissions").select("commission_amount,status,currency,gross_amount,created_at").eq("creator_id", creatorId),
        supabase.from("clicks").select("product_id,product:products(title,brand,image_key)").eq("creator_id", creatorId).gte("created_at", thirtyDaysAgo).limit(1000),
      ]);

      const c = (commissions.data ?? []) as { commission_amount: number; status: string; gross_amount: number }[];
      const pending = c.filter((r) => r.status === "pending").reduce((s, r) => s + Number(r.commission_amount || 0), 0);
      const approved = c.filter((r) => r.status === "approved").reduce((s, r) => s + Number(r.commission_amount || 0), 0);
      const paid = c.filter((r) => r.status === "paid").reduce((s, r) => s + Number(r.commission_amount || 0), 0);
      const gross = c.reduce((s, r) => s + Number(r.gross_amount || 0), 0);
      const orders = c.length;
      const conversion = (clicks30d.count ?? 0) > 0 ? (orders / (clicks30d.count ?? 1)) * 100 : 0;

      const counts = new Map<string, { count: number; product: { title: string; brand: string | null; image_key: string | null } | null }>();
      for (const row of topProducts.data ?? []) {
        const id = (row as { product_id: string }).product_id;
        const entry = counts.get(id) ?? { count: 0, product: (row as unknown as { product: { title: string; brand: string | null; image_key: string | null } | null }).product };
        entry.count += 1;
        counts.set(id, entry);
      }
      const top = Array.from(counts.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5)
        .map(([id, v]) => ({ id, count: v.count, product: v.product }));

      return {
        clicks7d: clicks7d.count ?? 0,
        clicks30d: clicks30d.count ?? 0,
        pending, approved, paid, gross, orders, conversion,
        top,
      };
    },
  });

  const fmt = (n: number) => { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n); } catch { return `£${n.toFixed(0)}`; } };
  const m = stats.data;

  return (
    <div className="space-y-8">
      {stats.isLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat icon={MousePointerClick} label="Clicks (7d)" value={m ? m.clicks7d.toLocaleString() : "…"} delta={m ? `${m.clicks30d.toLocaleString()} in 30d` : ""} />
          <Stat icon={TrendingUp} label="Conversion (30d)" value={m ? `${m.conversion.toFixed(1)}%` : "…"} delta={m ? `${m.orders} orders` : ""} />
          <Stat icon={PoundSterling} label="Pending earnings" value={m ? fmt(m.pending + m.approved) : "…"} delta={m ? `${fmt(m.approved)} approved` : ""} />
          <Stat icon={PoundSterling} label="Paid to date" value={m ? fmt(m.paid) : "…"} delta={m ? `${fmt(m.gross)} gross sales` : ""} />
        </div>
      )}

      <EarningsOverTime creatorId={creatorId} />

      <Milestones lifetimeEarnings={m ? m.approved + m.paid : 0} loading={stats.isLoading} />


      <div>
        <h3 className="font-serif text-xl">Top products (30d)</h3>
        {stats.isLoading ? (
          <div className="mt-4 divide-y divide-border border border-border bg-card">
            {Array.from({ length: 5 }).map((_, i) => <ListItemSkeleton key={i} />)}
          </div>
        ) : (m?.top.length ?? 0) === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No tracked clicks yet — share your storefront to start collecting data.</p>
        ) : (
          <div className="mt-4 divide-y divide-border border border-border bg-card">
            {m?.top.map((row, i) => (
              <div key={row.id} className="flex items-center gap-3 px-4 py-3">
                <span className="w-4 text-xs text-muted-foreground">{i + 1}</span>
                <img src={resolveImage(row.product?.image_key ?? null)} alt={row.product?.title ?? "Product"} className="h-10 w-10 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{row.product?.title ?? "Unknown"}</p>
                  <p className="truncate text-xs text-muted-foreground">{row.product?.brand}</p>
                </div>
                <span className="text-xs">{row.count} clicks</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EarningsOverTime({ creatorId }: { creatorId: string }) {
  const sixMonthsStart = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const earnings = useQuery({
    queryKey: ["creator-earnings-6m", creatorId, sixMonthsStart.toISOString()],
    queryFn: async () => {
      const { data } = await supabase
        .from("commissions")
        .select("commission_amount,created_at,status")
        .eq("creator_id", creatorId)
        .eq("status", "paid")
        .gte("created_at", sixMonthsStart.toISOString());
      const rows = (data ?? []) as { commission_amount: number; created_at: string }[];
      const buckets = new Map<string, number>();
      for (const r of rows) {
        const d = new Date(r.created_at);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        buckets.set(key, (buckets.get(key) ?? 0) + Number(r.commission_amount || 0));
      }
      const months: { month: string; amount: number }[] = [];
      const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      for (let i = 0; i < 6; i++) {
        const d = new Date(sixMonthsStart);
        d.setMonth(sixMonthsStart.getMonth() + i);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        months.push({ month: names[d.getMonth()], amount: Math.round(buckets.get(key) ?? 0) });
      }
      return months;
    },
  });

  const data = earnings.data ?? [];
  const fmtGbp = (n: number) => `£${n.toLocaleString("en-GB")}`;

  return (
    <div>
      <h3 className="font-serif text-2xl">Earnings over time</h3>
      <div className="mt-4 border border-border bg-card p-4" style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={48}
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              tickFormatter={(v) => fmtGbp(Number(v))}
            />
            <Tooltip
              cursor={{ fill: "color-mix(in oklab, var(--muted) 40%, transparent)" }}
              contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 12 }}
              formatter={(v: number) => [fmtGbp(Number(v)), "Earnings"]}
            />
            <Bar dataKey="amount" fill="var(--foreground)" radius={0}>
              {data.map((_, i) => (
                <Cell key={i} fill="var(--foreground)" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


// ----- CURATE -----

const MILESTONES_GBP_UI = [100, 500, 1000, 2500, 5000, 10000] as const;

function Milestones({ lifetimeEarnings, loading }: { lifetimeEarnings: number; loading: boolean }) {
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
  const next = MILESTONES_GBP_UI.find((m) => lifetimeEarnings < m);
  const prev = [...MILESTONES_GBP_UI].reverse().find((m) => lifetimeEarnings >= m) ?? 0;
  const pct = next ? Math.min(100, Math.max(0, ((lifetimeEarnings - prev) / (next - prev)) * 100)) : 100;

  return (
    <div>
      <h3 className="font-serif text-2xl">Milestones</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {loading
          ? "Loading lifetime earnings…"
          : next
            ? `${fmt(lifetimeEarnings)} of ${fmt(next)} to next milestone`
            : `You've cleared every milestone — ${fmt(lifetimeEarnings)} lifetime.`}
      </p>

      <div className="mt-4 border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-2">
          {MILESTONES_GBP_UI.map((m, i) => {
            const reached = lifetimeEarnings >= m;
            return (
              <div key={m} className="flex flex-1 flex-col items-center gap-2">
                <div
                  className={
                    "flex h-9 w-9 items-center justify-center rounded-full border text-[10px] font-medium tabular-nums " +
                    (reached
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-background text-muted-foreground")
                  }
                  aria-label={`Milestone ${fmt(m)}${reached ? " reached" : ""}`}
                >
                  {i + 1}
                </div>
                <span className={"text-[10px] uppercase tracking-widest " + (reached ? "text-foreground" : "text-muted-foreground")}>
                  {fmt(m)}
                </span>
              </div>
            );
          })}
        </div>

        {next && (
          <div className="mt-5">
            <div className="h-1.5 w-full overflow-hidden bg-muted">
              <div className="h-full bg-foreground transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{fmt(prev)}</span>
              <span>{fmt(next)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CATEGORIES = [
  "All",
  "Womens Fashion",
  "Beauty & Skincare",
  "Mother & Baby",
  "Home & Decor",
  "Fitness & Wellness",
  "Shoes & Accessories",
  "Cooking & Grocery",
  "Finance & Lifestyle",
  "Travel",
  "GRWM",
] as const;

function Curate({ creatorId }: { creatorId: string }) {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<CatalogFilters>({ category: "All", inStock: true, page: 0, pageSize: 24 });
  const [search, setSearch] = useState("");

  const catalog = useQuery({
    queryKey: ["catalog", filters],
    queryFn: () => searchCatalog(filters),
  });
  const mine = useQuery({
    queryKey: ["creator-products", creatorId],
    queryFn: () => fetchCreatorProducts(creatorId),
  });

  const mineIds = useMemo(() => new Set((mine.data ?? []).map((p) => p.id)), [mine.data]);

  const add = useMutation({
    mutationFn: async (productId: string) => {
      const position = (mine.data?.length ?? 0);
      const { error } = await supabase.from("creator_products").insert({ creator_id: creatorId, product_id: productId, position });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["creator-products", creatorId] }),
  });

  const remove = useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase.from("creator_products").delete().eq("creator_id", creatorId).eq("product_id", productId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["creator-products", creatorId] }),
  });

  const reorder = useMutation({
    mutationFn: async (ids: string[]) => reorderCreatorProducts(creatorId, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["creator-products", creatorId] }),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const list = (mine.data ?? []).map((p) => p.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= list.length) return;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    reorder.mutate(list);
  };

  const total = catalog.data?.total ?? 0;
  const pageSize = filters.pageSize ?? 24;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-12">
      {/* My storefront */}
      <section>
        <div className="flex items-baseline justify-between">
          <div>
            <h2 className="font-serif text-2xl">My storefront</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {mine.data?.length ?? 0} curated · drag chevrons to reorder
            </p>
          </div>
        </div>
        {mine.isLoading ? (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        ) : (mine.data ?? []).length === 0 ? (
          <div className="mt-6 rounded border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No products yet. Add some from the catalog below.
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            {(mine.data ?? []).map((p, i) => (
              <div key={p.id} className="group relative card-lift border border-border bg-card overflow-hidden rounded-sm">
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  <img src={resolveImage(p.image_key)} alt={p.title} className="ken-burns h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-3">
                  <p className="line-clamp-1 text-xs text-muted-foreground">{p.brand}</p>
                  <p className="line-clamp-2 text-xs">{p.title}</p>
                </div>
                <div className="absolute inset-x-1 top-1 flex justify-between opacity-0 transition group-hover:opacity-100">
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded bg-background/90 p-1 disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={() => move(i, +1)} disabled={i === (mine.data?.length ?? 1) - 1} className="rounded bg-background/90 p-1 disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
                  </div>
                  <button onClick={() => remove.mutate(p.id)} className="rounded bg-background/90 p-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Catalog */}
      <section>
        <h2 className="font-serif text-2xl">Affiliate catalog</h2>
        <p className="mt-1 text-xs text-muted-foreground">Synced from Levanta · {total.toLocaleString()} products</p>

        <form
          onSubmit={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, q: search, page: 0 })); }}
          className="mt-6 flex flex-wrap items-center gap-2"
        >
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title or brand…"
              className="w-full border border-border bg-card pl-9 pr-3 py-2 text-sm outline-none focus:border-foreground"
            />
          </div>
          <select
            value={filters.category ?? "All"}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value as any, page: 0 }))}
            className="border border-border bg-card px-3 py-2 text-sm"
          >
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={filters.inStock ?? false}
              onChange={(e) => setFilters((f) => ({ ...f, inStock: e.target.checked || undefined, page: 0 }))}
            />
            In stock
          </label>
          <input
            type="number"
            placeholder="Min £"
            className="w-20 border border-border bg-card px-2 py-2 text-sm"
            onChange={(e) => setFilters((f) => ({ ...f, minPrice: e.target.value ? Number(e.target.value) : undefined, page: 0 }))}
          />
          <input
            type="number"
            placeholder="Max £"
            className="w-20 border border-border bg-card px-2 py-2 text-sm"
            onChange={(e) => setFilters((f) => ({ ...f, maxPrice: e.target.value ? Number(e.target.value) : undefined, page: 0 }))}
          />
          <button type="submit" className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background">Search</button>
        </form>

        {catalog.isLoading ? (
          <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        ) : catalog.error ? (
          <p className="mt-6 text-sm text-destructive">{(catalog.error as Error).message}</p>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-6">
              {(catalog.data?.items ?? []).map((p) => {
                const owned = mineIds.has(p.id);
                return (
                  <div key={p.id} className="group card-lift border border-border bg-card overflow-hidden rounded-sm">
                    <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                      <img src={resolveImage(p.image_key)} alt={p.title} className="ken-burns h-full w-full object-cover" loading="lazy" />
                      <button
                        onClick={() => (owned ? remove.mutate(p.id) : add.mutate(p.id))}
                        className={`absolute inset-x-2 bottom-2 flex items-center justify-center gap-1 py-1.5 text-[10px] uppercase tracking-widest transition ${owned ? "bg-foreground text-background" : "bg-background/95"}`}
                      >
                        {owned ? <><X className="h-3 w-3" /> Remove</> : <><Plus className="h-3 w-3" /> Add</>}
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="line-clamp-1 text-xs text-muted-foreground">{p.brand}</p>
                      <p className="line-clamp-2 text-xs">{p.title}</p>
                      {p.commission && p.commission > 0 && (
                        <span className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          {p.commission}% commission
                        </span>
                      )}
                      <p className="text-[10px] text-accent">£{Number(p.price).toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            <div className="mt-8 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Page {(filters.page ?? 0) + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, (f.page ?? 0) - 1) }))}
                  disabled={(filters.page ?? 0) === 0}
                  className="border border-border px-4 py-1.5 uppercase tracking-widest disabled:opacity-30"
                >Prev</button>
                <button
                  onClick={() => setFilters((f) => ({ ...f, page: Math.min(totalPages - 1, (f.page ?? 0) + 1) }))}
                  disabled={(filters.page ?? 0) >= totalPages - 1}
                  className="border border-border px-4 py-1.5 uppercase tracking-widest disabled:opacity-30"
                >Next</button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// ----- COLLECTIONS -----

function Collections({ creatorId }: { creatorId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const { data: collections = [], isLoading: collectionsLoading } = useQuery({
    queryKey: ["collections", creatorId],
    queryFn: () => fetchCreatorCollections(creatorId),
  });
  const [title, setTitle] = useState("");

  const create = useMutation({
    mutationFn: async (overrideTitle?: string) => {
      const finalTitle = (overrideTitle ?? title).trim() || "Untitled collection";
      const { data, error } = await supabase
        .from("collections")
        .insert({ creator_id: creatorId, title: finalTitle })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      setTitle("");
      qc.invalidateQueries({ queryKey: ["collections", creatorId] });
      if (row?.id) setEditing(row.id);
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("collections").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections", creatorId] }),
  });

  if (editing) {
    return <CollectionEditor collectionId={editing} creatorId={creatorId} onBack={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-2xl">Themed bundles</h2>
        <form onSubmit={(e) => { e.preventDefault(); if (create.isPending) return; create.mutate(title.trim() || "Untitled collection"); }} className="flex gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Collection title" className="border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground" />
          <button type="submit" disabled={create.isPending} className="flex items-center gap-2 bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-60"><Plus className="h-3 w-3" /> {create.isPending ? "Creating…" : "New"}</button>
        </form>
      </div>
      {collectionsLoading ? (
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <CollectionSkeleton key={i} />)}
        </div>
      ) : (
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {collections.map((c) => (
            <div key={c.id} className="border border-border bg-card overflow-hidden rounded-sm">
              <button onClick={() => setEditing(c.id)} className="block w-full text-left">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={resolveImage(c.cover_url)} alt={c.title} className="h-full w-full object-cover transition hover:scale-105" />
                </div>
              </button>
              <div className="flex items-center justify-between p-5">
                <div>
                  <p className="font-serif text-xl">{c.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.collection_products.length} pieces</p>
                </div>
                <div className="flex gap-2 text-muted-foreground">
                  <button onClick={() => setEditing(c.id)} className="hover:text-foreground" title="Edit"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => setDeleteTarget(c.id)} className="hover:text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
          {collections.length === 0 && (
            <div className="md:col-span-3 rounded border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Bundle products into themed collections like "Spring beauty edit" or "Cosy at home".
            </div>
          )}
        </div>
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete collection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the collection and all its products. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) del.mutate(deleteTarget);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CollectionEditor({ collectionId, creatorId, onBack }: { collectionId: string; creatorId: string; onBack: () => void }) {
  const qc = useQueryClient();
  const collection = useQuery({
    queryKey: ["collection", collectionId],
    queryFn: () => fetchCollection(collectionId),
  });
  const curated = useQuery({
    queryKey: ["creator-products", creatorId],
    queryFn: () => fetchCreatorProducts(creatorId),
  });

  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [pendingCover, setPendingCover] = useState<string | null>(null);

  const items: any[] = (collection.data?.items ?? []) as any[];
  const inCollectionIds = new Set(items.map((i) => i.product.id));

  const save = useMutation({
    mutationFn: async (orderedIds: string[]) => setCollectionProducts(collectionId, orderedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collection", collectionId] });
      qc.invalidateQueries({ queryKey: ["collections", creatorId] });
    },
  });

  const updateMeta = useMutation({
    mutationFn: async (patch: { title?: string; cover_url?: string | null }) => updateCollection(collectionId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collection", collectionId] });
      qc.invalidateQueries({ queryKey: ["collections", creatorId] });
    },
  });

  const toggle = (productId: string) => {
    const current = items.map((i) => i.product.id);
    const next = current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId];
    save.mutate(next);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const list = items.map((i) => i.product.id);
    [list[idx], list[swap]] = [list[swap], list[idx]];
    save.mutate(list);
  };

  if (collection.isLoading || !collection.data) {
    return <CollectionEditorSkeleton />;
  }

  const title = pendingTitle ?? collection.data.title;
  const cover = pendingCover ?? collection.data.cover_url ?? "";

  return (
    <div className="space-y-10">
      <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> All collections
      </button>

      {/* Meta */}
      <div className="grid gap-6 md:grid-cols-[260px_1fr]">
        <div className="aspect-[4/5] overflow-hidden bg-muted">
          <img src={resolveImage(cover || collection.data.cover_url)} alt={collection.data.title} className="h-full w-full object-cover" />
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Title</label>
            <input
              value={title}
              onChange={(e) => setPendingTitle(e.target.value)}
              onBlur={() => { if (pendingTitle !== null && pendingTitle !== collection.data!.title) updateMeta.mutate({ title: pendingTitle }); }}
              className="mt-1 w-full border border-border bg-card px-3 py-2 text-lg font-serif outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Cover image URL</label>
            <input
              value={cover}
              onChange={(e) => setPendingCover(e.target.value)}
              onBlur={() => { if (pendingCover !== null && pendingCover !== (collection.data!.cover_url ?? "")) updateMeta.mutate({ cover_url: pendingCover || null }); }}
              placeholder="Paste an image URL or use a product image"
              className="mt-1 w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
            />
            <p className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">Quick pick:
              {items.slice(0, 6).map((i) => (
                <button key={i.product.id} onClick={() => { setPendingCover(i.product.image_key); updateMeta.mutate({ cover_url: i.product.image_key }); }} className="underline hover:text-foreground">
                  {i.product.brand}
                </button>
              ))}
            </p>
          </div>
        </div>
      </div>

      {/* Selected items */}
      <section>
        <h3 className="font-serif text-xl">In this collection ({items.length})</h3>
        {items.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Pick from your curated products below.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
            {items.map((row, i) => (
              <div key={row.product.id} className="group relative">
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  <img src={resolveImage(row.product.image_key)} alt={row.product.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{row.product.brand}</p>
                <p className="line-clamp-2 text-xs">{row.product.title}</p>
                <div className="absolute inset-x-1 top-1 flex justify-between opacity-0 transition group-hover:opacity-100">
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)} className="rounded bg-background/90 p-1"><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={() => move(i, +1)} className="rounded bg-background/90 p-1"><ChevronDown className="h-3 w-3" /></button>
                  </div>
                  <button onClick={() => toggle(row.product.id)} className="rounded bg-background/90 p-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Available from curated */}
      <section>
        <h3 className="font-serif text-xl">Add from your storefront</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Only products you've already curated can be added to a collection.
          {(curated.data ?? []).length === 0 && " Add some in the Curate tab first."}
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-6">
          {(curated.data ?? []).map((p) => {
            const inside = inCollectionIds.has(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                className={`group text-left ${inside ? "opacity-40" : ""}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img src={resolveImage(p.image_key)} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
                  <div className={`absolute inset-x-1 bottom-1 flex items-center justify-center gap-1 py-1 text-[10px] uppercase tracking-widest ${inside ? "bg-foreground text-background" : "bg-background/95"}`}>
                    {inside ? <><X className="h-3 w-3" /> In</> : <><Plus className="h-3 w-3" /> Add</>}
                  </div>
                </div>
                <p className="mt-1 line-clamp-1 text-xs">{p.brand}</p>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ----- SMART LINKS -----

type ProductMatch = {
  id: string;
  title: string;
  brand: string;
  image_key: string | null;
  url: string;
};

function normaliseUrl(raw: string): string | null {
  try {
    const u = new URL(raw.trim());
    // strip tracking params
    ["tag", "ref", "ref_", "linkCode", "ascsubtag", "subid"].forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return null;
  }
}

function extractAsin(url: string): string | null {
  const m = url.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i);
  return m ? m[1].toUpperCase() : null;
}

function CopyButton({ text }: { text: string }) {
  return (
    <SharedCopyButton
      content={text}
      size="sm"
      variant="outline"
      className="h-8 px-3 text-xs uppercase tracking-widest"
    >
      Copy
    </SharedCopyButton>
  );
}

function SmartLinks({ handle, creatorId }: { handle: string; creatorId: string }) {
  const [input, setInput] = useState("");
  const [match, setMatch] = useState<ProductMatch | null>(null);
  const [status, setStatus] = useState<"idle" | "searching" | "no-match" | "found">("idle");

  const base = typeof window !== "undefined" ? window.location.origin : "";
  const storefrontUrl = `${base}/creator/${handle}`;

  async function lookup(raw: string) {
    setMatch(null);
    const url = normaliseUrl(raw);
    if (!url) { setStatus("no-match"); return; }
    setStatus("searching");

    const asin = extractAsin(url);
    let row: ProductMatch | null = null;

    if (asin) {
      const { data } = await supabase
        .from("products")
        .select("id,title,brand,image_key,url")
        .eq("external_id", asin)
        .maybeSingle();
      if (data) row = data as ProductMatch;
    }
    if (!row) {
      const { data } = await supabase
        .from("products")
        .select("id,title,brand,image_key,url")
        .ilike("url", `%${new URL(url).pathname.split("/").filter(Boolean).slice(0, 3).join("/")}%`)
        .limit(1);
      if (data && data[0]) row = data[0] as ProductMatch;
    }

    setMatch(row);
    setStatus(row ? "found" : "no-match");
  }

  // Top tracked links (last 30d)
  const top = useQuery({
    queryKey: ["smart-link-top", handle],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data: creator } = await supabase.from("creators").select("id").eq("handle", handle).maybeSingle();
      if (!creator) return [];
      const { data } = await supabase
        .from("clicks")
        .select("product_id, product:products(id,title,brand,image_key)")
        .eq("creator_id", creator.id)
        .gte("created_at", since)
        .limit(500);
      const counts = new Map<string, { count: number; product: any }>();
      for (const row of (data ?? []) as any[]) {
        const e = counts.get(row.product_id) ?? { count: 0, product: row.product };
        e.count += 1;
        counts.set(row.product_id, e);
      }
      return Array.from(counts.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 8)
        .map(([id, v]) => ({ id, count: v.count, product: v.product }));
    },
  });

  return (
    <div className="max-w-3xl space-y-10">
      <div>
        <h2 className="font-serif text-2xl">Smart Link generator</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Paste any product URL from the affiliate catalog. We'll generate a tracked link
          that records the click against your storefront and rewrites it with your sub-id.
        </p>
      </div>

      <ReferralSection creatorId={creatorId} />

      <section className="border border-border bg-card p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Your storefront</p>
        <div className="flex items-center justify-between gap-3">
          <code className="truncate text-sm">{storefrontUrl}</code>
          <CopyButton text={storefrontUrl} />
        </div>
      </section>

      <EmbedStorefrontSection handle={handle} />

      <section className="border border-border bg-card p-5 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Link in Bio URL</p>
          <p className="mt-1 text-sm text-muted-foreground">
            A mobile-first, distraction-free version of your storefront — perfect for Instagram and TikTok.
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <code className="truncate text-sm">{`${base}/bio/${handle}`}</code>
          <CopyButton text={`${base}/bio/${handle}`} />
        </div>
        <div className="flex items-center gap-4 border-t border-border pt-4">
          <div className="flex flex-col items-center gap-2">
            <div className="qr-wrapper">
              <QRCodeSVG
                value={`${base}/bio/${handle}`}
                size={96}
                bgColor="transparent"
                fgColor="currentColor"
              />
            </div>
            <button
              onClick={() => {
                const wrapper = document.querySelector(".qr-wrapper");
                const svg = wrapper?.querySelector("svg");
                if (!svg) return;
                const serializer = new XMLSerializer();
                const svgStr = serializer.serializeToString(svg);
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                const img = new Image();
                img.onload = () => {
                  canvas.width = 384;
                  canvas.height = 384;
                  ctx.fillStyle = "#ffffff";
                  ctx.fillRect(0, 0, 384, 384);
                  ctx.drawImage(img, 0, 0, 384, 384);
                  const a = document.createElement("a");
                  a.href = canvas.toDataURL("image/png");
                  a.download = "pluggz-qr.png";
                  a.click();
                };
                img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));
              }}
              className="text-xs uppercase tracking-widest border border-border px-3 py-1.5 hover:bg-muted transition-colors"
            >
              Download QR
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            Share this link in your Instagram and TikTok bio.
          </p>
        </div>
      </section>

      <section className="space-y-3">
        <form
          onSubmit={(e) => { e.preventDefault(); lookup(input); }}
          className="flex flex-wrap items-center gap-2"
        >
          <div className="relative flex-1 min-w-[240px]">
            <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="https://www.amazon.co.uk/dp/B0XXXXXXXX"
              className="w-full border border-border bg-card pl-9 pr-3 py-2 text-sm outline-none focus:border-foreground"
            />
          </div>
          <button type="submit" disabled={!input || status === "searching"} className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-40">
            {status === "searching" ? "Searching…" : "Generate link"}
          </button>
        </form>

        {status === "no-match" && (
          <p className="text-sm text-destructive">
            We couldn't match that URL to a synced product. Try a different one or add it from the Curate tab.
          </p>
        )}

        {status === "found" && match && (
          <div className="border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <img src={resolveImage(match.image_key)} alt={match.title} className="h-16 w-16 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{match.brand}</p>
                <p className="line-clamp-2 text-sm">{match.title}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
              <code className="truncate text-sm">{`${base}/r/${handle}/${match.id}`}</code>
              <CopyButton text={`${base}/r/${handle}/${match.id}`} />
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="font-serif text-xl">Top tracked links · 30d</h3>
        {top.isLoading ? (
          <div className="mt-4 divide-y divide-border border border-border bg-card">
            {Array.from({ length: 5 }).map((_, i) => <ListItemSkeleton key={i} />)}
          </div>
        ) : (top.data?.length ?? 0) === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No tracked clicks yet — share a smart link to start collecting data.</p>
        ) : (
          <div className="mt-4 divide-y divide-border border border-border bg-card">
            {top.data?.map((row) => {
              const trackedUrl = `${base}/r/${handle}/${row.id}`;
              return (
                <div key={row.id} className="flex items-center gap-3 px-4 py-3">
                  <img src={resolveImage(row.product?.image_key ?? null)} alt={row.product?.title ?? "Product"} className="h-10 w-10 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{row.product?.title ?? "Unknown"}</p>
                    <p className="truncate text-xs text-muted-foreground">{row.product?.brand}</p>
                  </div>
                  <span className="text-xs text-muted-foreground tabular-nums">{row.count} clicks</span>
                  <CopyButton text={trackedUrl} />
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function EmbedStorefrontSection({ handle }: { handle: string }) {
  const [limit, setLimit] = useState<6 | 8 | 12>(6);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [showPreview, setShowPreview] = useState(false);

  const embedSrc = `https://www.pluggzofficial.com/embed/${handle}?limit=${limit}&theme=${theme}`;
  const iframeCode = `<iframe src="${embedSrc}" width="100%" height="600" frameborder="0" scrolling="no" style="border:none;"></iframe>`;

  return (
    <section className="border border-border bg-card p-5 space-y-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Embed your storefront</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste this code on your website to show your Pluggz products.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Limit</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) as 6 | 8 | 12)}
            className="border border-border bg-background px-2 py-1"
          >
            <option value={6}>6</option>
            <option value={8}>8</option>
            <option value={12}>12</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Theme</span>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as "light" | "dark")}
            className="border border-border bg-background px-2 py-1"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="ml-auto border border-border px-3 py-1 text-xs uppercase tracking-widest hover:bg-background"
        >
          {showPreview ? "Hide preview" : "Show preview"}
        </button>
      </div>

      <div className="space-y-2">
        <pre className="overflow-x-auto whitespace-pre-wrap break-all border border-border bg-background p-3 text-xs">
{iframeCode}
        </pre>
        <div className="flex justify-end">
          <CopyButton text={iframeCode} />
        </div>
      </div>

      {showPreview && (
        <div className="border border-border bg-background">
          <iframe
            title="Storefront embed preview"
            src={`/embed/${handle}?limit=${limit}&theme=${theme}`}
            width="100%"
            height={600}
            sandbox="allow-scripts allow-same-origin allow-popups"
            style={{ border: "none", display: "block" }}
          />
        </div>
      )}
    </section>
  );
}

// ----- PROFILE EDITOR -----

type CreatorProfile = {
  id: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  profile_video_url: string | null;
  instagram: string | null;
  tiktok: string | null;
  youtube: string | null;
  website: string | null;
  followers: string | null;
  categories: string[] | null;
  is_verified: boolean | null;
};

const PROFILE_CATEGORIES = [
  "Womens Fashion",
  "Beauty & Skincare",
  "Mother & Baby",
  "Home & Decor",
  "Fitness & Wellness",
  "Shoes & Accessories",
  "Cooking & Grocery",
  "Finance & Lifestyle",
  "Travel",
  "GRWM",
] as const;

function safeUrl(val: string | null | undefined): string | null {
  if (!val) return null;
  try {
    const u = new URL(val);
    if (!["http:", "https:"].includes(u.protocol)) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function ProfileEditor({ creatorId }: { creatorId: string }) {
  const qc = useQueryClient();
  const profile = useQuery({
    queryKey: ["creator-profile", creatorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("id,name,bio,avatar_url,cover_url,profile_video_url,instagram,tiktok,youtube,website,followers,categories,is_verified")
        .eq("id", creatorId)
        .single();
      if (error) throw error;
      return data as CreatorProfile;
    },
  });

  const [form, setForm] = useState<Partial<CreatorProfile> | null>(null);
  const current: Partial<CreatorProfile> = form ?? profile.data ?? {};
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (patch: Partial<CreatorProfile>) => {
      const next = { ...patch };
      if (avatarFile || coverFile) {
        const { data: u } = await supabase.auth.getUser();
        const uid = u.user?.id;
        if (!uid) throw new Error("Not signed in");
        if (avatarFile) {
          next.avatar_url = await uploadLookImageAtKey(avatarFile, `${uid}/avatar`);
        }
        if (coverFile) {
          next.cover_url = await uploadLookImageAtKey(coverFile, `${uid}/cover`);
        }
      }
      const sanitized = {
        ...next,
        avatar_url: safeUrl(next.avatar_url),
        cover_url: safeUrl(next.cover_url),
        profile_video_url: safeUrl(next.profile_video_url),
        website: safeUrl(next.website),
      };
      const { error } = await supabase.from("creators").update(sanitized as never).eq("id", creatorId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["creator-profile", creatorId] });
      qc.invalidateQueries({ queryKey: ["my-creator"] });
      qc.invalidateQueries({ queryKey: ["creators"] });
      setForm(null);
      setAvatarFile(null);
      setCoverFile(null);
      setAvatarPreview(null);
      setCoverPreview(null);
    },
  });

  if (profile.isLoading) return <ProfileSkeleton />;
  if (profile.error) return <p className="text-sm text-destructive">{(profile.error as Error).message}</p>;

  const set = <K extends keyof CreatorProfile>(key: K, value: CreatorProfile[K]) =>
    setForm((f) => ({ ...(f ?? profile.data!), [key]: value }));

  const toggleCategory = (cat: string) => {
    const arr = new Set(current.categories ?? []);
    if (arr.has(cat)) arr.delete(cat); else arr.add(cat);
    set("categories", Array.from(arr));
  };

  const isDirty = form !== null || avatarFile !== null || coverFile !== null;

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h2 className="font-serif text-2xl">Profile</h2>
        <p className="mt-1 text-sm text-muted-foreground">How you appear across Pluggz.</p>
        <div className="mt-3">
          {current.is_verified ? (
            <span className="inline-flex items-center gap-2 border border-foreground bg-foreground px-3 py-1 text-[10px] uppercase tracking-widest text-background">
              <VerifiedBadge /> Verified
            </span>
          ) : (
            <a
              href="#social-verification"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("social-verification")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground underline hover:text-foreground"
            >
              Verify your socials to get your badge →
            </a>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr]">
        <div className="space-y-3">
          <Field label="Display name">
            <input value={current.name ?? ""} onChange={(e) => set("name", e.target.value)} className="w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground" />
          </Field>
          <Field label="Bio" hint="Shown on your storefront. Keep it short and personal.">
            <textarea value={current.bio ?? ""} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={500} className="w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground" />
          </Field>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label="Avatar">
          <ImageUploadField
            shape="circle"
            value={current.avatar_url}
            preview={avatarPreview}
            uploading={save.isPending && !!avatarFile}
            onFile={(f, dataUrl) => { setAvatarFile(f); setAvatarPreview(dataUrl); }}
            onUrlChange={(v) => set("avatar_url", v)}
          />
        </Field>
        <Field label="Cover image">
          <ImageUploadField
            shape="rect"
            value={current.cover_url}
            preview={coverPreview}
            uploading={save.isPending && !!coverFile}
            onFile={(f, dataUrl) => { setCoverFile(f); setCoverPreview(dataUrl); }}
            onUrlChange={(v) => set("cover_url", v)}
          />
        </Field>
        <Field label="Profile video URL" hint="MP4 / YouTube / Vimeo — shown above the bio."><UrlInput value={current.profile_video_url} onChange={(v) => set("profile_video_url", v)} placeholder="https://…" /></Field>
        <Field label="Website"><UrlInput value={current.website} onChange={(v) => set("website", v)} placeholder="https://…" /></Field>
        <Field label="Instagram handle"><HandleInput value={current.instagram} onChange={(v) => set("instagram", v)} prefix="@" /></Field>
        <Field label="TikTok handle"><HandleInput value={current.tiktok} onChange={(v) => set("tiktok", v)} prefix="@" /></Field>
        <Field label="YouTube channel"><HandleInput value={current.youtube} onChange={(v) => set("youtube", v)} prefix="@" /></Field>
        <Field label="Followers (display only)"><input value={current.followers ?? ""} onChange={(e) => set("followers", e.target.value)} placeholder="e.g. 24k" className="w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground" /></Field>
      </div>

      <div id="social-verification">
        <SocialVerification creatorId={creatorId} />
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Categories</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {PROFILE_CATEGORIES.map((c) => {
            const on = (current.categories ?? []).includes(c);
            return (
              <button key={c} onClick={() => toggleCategory(c)} className={`border px-3 py-1.5 text-xs uppercase tracking-widest ${on ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}>{c}</button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-border pt-6">
        <button
          onClick={() => save.mutate(form ?? {})}
          disabled={!isDirty || save.isPending}
          className="bg-foreground px-6 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {isDirty && (
          <button onClick={() => { setForm(null); setAvatarFile(null); setCoverFile(null); setAvatarPreview(null); setCoverPreview(null); }} className="border border-border px-6 py-2 text-xs uppercase tracking-widest">Discard</button>
        )}
        {save.error && <p className="text-xs text-destructive">{(save.error as Error).message}</p>}
        {save.isSuccess && !isDirty && <p className="text-xs text-emerald-700">Saved.</p>}
      </div>
    </div>
  );
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

function ImageUploadField({
  shape,
  value,
  preview,
  uploading,
  onFile,
  onUrlChange,
}: {
  shape: "circle" | "rect";
  value: string | null | undefined;
  preview: string | null;
  uploading: boolean;
  onFile: (file: File, dataUrl: string) => void;
  onUrlChange: (v: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const displaySrc = preview ?? (value ? resolveImage(value) : null);
  const previewClass =
    shape === "circle"
      ? "h-32 w-32 rounded-full object-cover border border-border bg-muted"
      : "h-32 w-full max-w-xs rounded object-cover border border-border bg-muted aspect-[3/1]";

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Image must be JPG, PNG or WebP");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onFile(file, reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      {displaySrc ? (
        <img src={displaySrc} alt="Profile photo preview" className={previewClass} />
      ) : (
        <div className={`${previewClass} flex items-center justify-center text-[10px] uppercase tracking-widest text-muted-foreground`}>
          No image
        </div>
      )}
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs uppercase tracking-widest hover:border-foreground disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {uploading ? "Uploading…" : "Upload photo"}
        </button>
      </div>
      <div>
        <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Or paste an image URL</p>
        <UrlInput value={value} onChange={onUrlChange} placeholder="https://…" />
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </label>
  );
}

function UrlInput({ value, onChange, placeholder }: { value: string | null | undefined; onChange: (v: string | null) => void; placeholder?: string }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={placeholder}
      className="w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
    />
  );
}

function HandleInput({ value, onChange, prefix }: { value: string | null | undefined; onChange: (v: string | null) => void; prefix: string }) {
  return (
    <div className="flex items-center border border-border bg-card focus-within:border-foreground">
      <span className="px-2 text-sm text-muted-foreground">{prefix}</span>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.replace(/^@/, "") || null)}
        className="w-full bg-transparent py-2 pr-3 text-sm outline-none"
      />
    </div>
  );
}

// ----- PAYOUTS -----

type PayoutMethod = "bank" | "paypal" | "stripe";

function Payouts({ creatorId }: { creatorId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [navigating, setNavigating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const me = useQuery({
    queryKey: ["creator-payouts-settings", creatorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("payout_method,payout_email,payout_details")
        .eq("id", creatorId)
        .single();
      if (error) throw error;
      return data as { payout_method: string | null; payout_email: string | null; payout_details: Record<string, string> | null };
    },
  });

  const history = useQuery({
    queryKey: ["creator-commission-history", creatorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("commissions")
        .select("id,created_at,posted_at,status,currency,commission_amount,gross_amount,external_order_id,network")
        .eq("creator_id", creatorId)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const platformSettings = useQuery({
    queryKey: ["platform-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("minimum_payout_amount")
        .eq("id", true)
        .maybeSingle();
      if (error) throw error;
      return data as { minimum_payout_amount: number } | null;
    },
  });

  const [method, setMethod] = useState<PayoutMethod | "">("");
  const [email, setEmail] = useState("");
  const [details, setDetails] = useState<Record<string, string>>({});

  // Hydrate once loaded
  const settings = me.data;
  const initialised = useMemo(() => settings?.payout_method ?? null, [settings]);
  useEffect(() => {
    if (settings && method === "") {
      setMethod((settings.payout_method as PayoutMethod) ?? "");
      setEmail(settings.payout_email ?? "");
      setDetails(settings.payout_details ?? {});
    }
  }, [settings, method]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("creators").update({
        payout_method: method || null,
        payout_email: email || null,
        payout_details: Object.keys(details).length ? details : null,
      }).eq("id", creatorId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["creator-payouts-settings", creatorId] }),
  });

  const fmt = (n: number, ccy = "GBP") => { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(n); } catch { return `£${n.toFixed(2)}`; } };
  const totals = useMemo(() => {
    const t = { tracked: 0, pending: 0, withdrawable: 0, approved: 0, paid: 0 };
    for (const r of history.data ?? []) {
      const a = Number(r.commission_amount || 0);
      if (r.status === "tracked") t.tracked += a;
      else if (r.status === "pending") t.pending += a;
      else if (r.status === "withdrawable") t.withdrawable += a;
      else if (r.status === "approved") t.approved += a;
      else if (r.status === "paid") t.paid += a;
    }
    return t;
  }, [history.data]);

  return (
    <div className="max-w-4xl space-y-10">
      <div>
        <h2 className="font-serif text-2xl">Payouts</h2>
        <p className="mt-1 text-sm text-muted-foreground">How you get paid + your commission history.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Stat icon={PoundSterling} label="Tracked" value={fmt(totals.tracked)} delta="Unvalidated by the network" />
        <Stat icon={PoundSterling} label="Pending" value={fmt(totals.pending)} delta="In holding window" />
        <Stat icon={PoundSterling} label="Withdrawable" value={fmt(totals.withdrawable + totals.approved)} delta="Ready to request" />
      </div>
      <p className="text-xs text-muted-foreground">
        Paid to date: {fmt(totals.paid)} · {history.data?.filter((r) => r.status === "paid").length ?? 0} payouts
      </p>

      {(totals.withdrawable + totals.approved) > (platformSettings.data?.minimum_payout_amount ?? 25) && (
        <div className="space-y-1">
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={navigating}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {navigating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Request payout
          </button>
          <p className="text-xs text-muted-foreground">
            Minimum payout: {fmt(platformSettings.data?.minimum_payout_amount ?? 25)}
          </p>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request payout?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>You are about to request a payout. This will take you to the Stripe Connect payout flow.</p>
                {history.isLoading ? (
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="h-4 w-16 animate-pulse bg-muted rounded" />
                        <span className="h-4 w-20 animate-pulse bg-muted rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tracked</span>
                      <span className="tabular-nums">{fmt(totals.tracked)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pending</span>
                      <span className="tabular-nums">{fmt(totals.pending)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Withdrawable</span>
                      <span className="font-medium tabular-nums">{fmt(totals.withdrawable + totals.approved)}</span>
                    </div>
                    <div className="border-t pt-1 mt-1 flex justify-between">
                      <span className="text-muted-foreground">Approved</span>
                      <span className="tabular-nums">{fmt(totals.approved)}</span>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setNavigating(true);
                navigate({ to: "/creator-payouts" });
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section className="border border-border bg-card p-8 text-center space-y-4">
        <h3 className="font-serif text-xl">Manage your payouts and Stripe connection</h3>
        <p className="text-sm text-muted-foreground">
          Use the dedicated payouts page to connect your Stripe account and manage withdrawals.
        </p>
        <Link
          to="/creator-payouts"
          className="inline-flex items-center gap-2 bg-foreground px-6 py-2 text-xs uppercase tracking-widest text-background hover:opacity-90"
        >
          Go to Payouts →
        </Link>
      </section>

      <section>
        <h3 className="font-serif text-xl">Commission history</h3>
        <div className="mt-4 overflow-x-auto border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Network</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2 text-right">Commission</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.isLoading && Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={6} className="border-t border-border" />)}
              {!history.isLoading && (history.data?.length ?? 0) === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No commissions yet.</td></tr>
              )}
              {history.data?.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.external_order_id ?? r.id.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{r.network}</td>
                  <td className="px-4 py-2 text-right">{fmt(Number(r.gross_amount), r.currency)}</td>
                  <td className="px-4 py-2 text-right">{fmt(Number(r.commission_amount), r.currency)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      r.status === "paid" ? "bg-emerald-700 text-white" :
                      r.status === "approved" ? "bg-sky-100 text-sky-900" :
                      r.status === "cancelled" ? "bg-rose-100 text-rose-900" :
                      "bg-amber-100 text-amber-900"
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AccountSettings({ creatorId }: { creatorId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Email change
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    setEmailLoading(false);
    if (error) { toast.error(error.message); return; }
    setEmailSent(true);
    toast.success("Confirmation sent to new address");
  };

  // Password change
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
    setPwLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setPwLoading(false);
    if (error) { toast.error(error.message); return; }
    setPw1(""); setPw2("");
    toast.success("Password updated");
  };

  // Notification preferences
  const { data: prefsRow } = useQuery({
    queryKey: ["creator-notification-prefs", creatorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("notification_preferences")
        .eq("id", creatorId)
        .single();
      if (error) throw error;
      return (data?.notification_preferences ?? {}) as Record<string, boolean>;
    },
  });
  const prefs = prefsRow ?? {};

  const savePrefs = useMutation({
    mutationFn: async (next: Record<string, boolean>) => {
      const { error } = await supabase
        .from("creators")
        .update({ notification_preferences: next })
        .eq("id", creatorId);
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      qc.setQueryData(["creator-notification-prefs", creatorId], next);
      toast.success("Preferences saved");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Failed to save"),
  });

  const togglePref = (key: string, value: boolean) => {
    savePrefs.mutate({ ...prefs, [key]: value });
  };

  const notifKeys: { key: string; label: string }[] = [
    { key: "notify_commission", label: "Email me when a commission is earned" },
    { key: "notify_payout", label: "Email me when a payout is sent" },
    { key: "notify_weekly", label: "Weekly performance digest" },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Email */}
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl">Email address</h2>
        <p className="mt-2 text-sm text-muted-foreground">{user?.email}</p>
        {!editingEmail ? (
          <button
            onClick={() => { setEditingEmail(true); setEmailSent(false); setNewEmail(""); }}
            className="mt-4 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted"
          >
            Change email
          </button>
        ) : (
          <form onSubmit={submitEmail} className="mt-4 space-y-3">
            <label htmlFor="new-email" className="block text-xs uppercase tracking-widest text-muted-foreground">New email</label>
            <input
              id="new-email"
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={emailLoading}
                className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-50"
              >
                {emailLoading ? "Sending..." : "Send confirmation"}
              </button>
              <button
                type="button"
                onClick={() => setEditingEmail(false)}
                className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted"
              >
                Cancel
              </button>
            </div>
            {emailSent && (
              <p className="text-xs text-accent">Confirmation sent to new address</p>
            )}
          </form>
        )}
      </section>

      {/* Password */}
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl">Change password</h2>
        <form onSubmit={submitPassword} className="mt-4 space-y-3">
          <div>
            <label htmlFor="new-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">New password</label>
            <input
              id="new-password"
              type="password"
              minLength={8}
              required
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              minLength={8}
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={pwLoading}
            className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-50"
          >
            {pwLoading ? "Updating..." : "Update password"}
          </button>
        </form>
      </section>

      {/* Two-factor authentication */}
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl">Two-factor authentication</h2>
        <div className="mt-4">
          <TwoFactorSettings />
        </div>
        <div className="mt-4">
          <AuthMethodBadge />
        </div>
      </section>

      {/* Notification preferences */}
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl">Notification preferences</h2>
        <div className="mt-4 space-y-4">
          {notifKeys.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <label htmlFor={`pref-${key}`} className="text-sm">{label}</label>
              <Switch
                id={`pref-${key}`}
                checked={!!prefs[key]}
                onCheckedChange={(v) => togglePref(key, v)}
                disabled={savePrefs.isPending}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Danger zone */}
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl text-rose-700">Danger zone</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Account deletion is handled in Privacy Settings.
        </p>
        <button
          onClick={() => navigate({ to: "/privacy-settings" })}
          className="mt-4 border border-rose-700 px-4 py-2 text-xs uppercase tracking-widest text-rose-700 hover:bg-rose-700 hover:text-white"
        >
          Delete my account
        </button>
      </section>
    </div>
  );
}

function Partnerships() {
  const qc = useQueryClient();
  const listFn = useServerFn(listMyPartnerships);
  const updateFn = useServerFn(updatePartnershipStatus);

  const { data, isLoading } = useQuery({
    queryKey: ["my-partnerships"],
    queryFn: () => listFn(),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; status: "accepted" | "declined" }) =>
      updateFn({ data: vars }),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === "accepted" ? "Offer accepted" : "Offer declined");
      qc.invalidateQueries({ queryKey: ["my-partnerships"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update"),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (!data || data.length === 0) {
    return (
      <div className="border border-border bg-card p-8 text-center">
        <p className="font-serif text-xl">No partnership offers yet</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Complete your profile to attract brand partnerships.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {data.map((p: Partnership) => (
        <article key={p.id} className="border border-border bg-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {p.brand_name}
              </p>
              <h3 className="mt-1 font-serif text-lg">{p.title}</h3>
            </div>
            <PartnershipBadge status={p.status} />
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{p.message}</p>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {p.fixed_fee_gbp != null && <span>Fee: £{Number(p.fixed_fee_gbp).toFixed(2)}</span>}
            {p.commission_rate != null && (
              <span>Commission: {(Number(p.commission_rate) * 100).toFixed(2)}%</span>
            )}
          </div>
          {p.status === "pending" && (
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate({ id: p.id, status: "accepted" })}
                className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-40"
              >
                Accept
              </button>
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate({ id: p.id, status: "declined" })}
                className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted disabled:opacity-40"
              >
                Decline
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function PartnershipBadge({ status }: { status: Partnership["status"] }) {
  if (status === "accepted") {
    return (
      <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-900">
        Active
      </span>
    );
  }
  const tone: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    declined: "bg-rose-100 text-rose-900",
    completed: "bg-slate-200 text-slate-900",
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone[status] ?? "bg-muted"}`}>
      {status}
    </span>
  );
}

function ReferralSection({ creatorId }: { creatorId: string }) {
  const meta = useQuery({
    queryKey: ["creator-referral-meta", creatorId],
    queryFn: async () => {
      const { data: c } = await supabase
        .from("creators")
        .select("referral_code")
        .eq("id", creatorId)
        .maybeSingle();
      const { data: refs } = await supabase
        .from("referrals")
        .select("id, status, bonus_gbp, created_at, referred:creators!referrals_referred_creator_id_fkey(handle, status)")
        .eq("referrer_creator_id", creatorId)
        .order("created_at", { ascending: false });
      return { code: c?.referral_code as string | null, referrals: (refs ?? []) as any[] };
    },
  });

  const code = meta.data?.code;
  const link = code ? `https://pluggzofficial.com/apply?ref=${code}` : null;
  const earned = (meta.data?.referrals ?? [])
    .filter((r) => r.status === "paid")
    .reduce((s, r) => s + Number(r.bonus_gbp ?? 0), 0);

  return (
    <section className="border border-border bg-card p-5 space-y-4">
      <div>
        <h3 className="font-serif text-xl">Earn £25 per referral</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Share your referral link. When a creator you refer gets approved, you earn £25 added to your next payout.
        </p>
      </div>

      {link ? (
        <div className="flex items-center justify-between gap-3">
          <code className="truncate text-sm">{link}</code>
          <CopyButton text={link} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Your referral link will appear once your account is approved.
        </p>
      )}

      {(meta.data?.referrals ?? []).length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Your referrals</p>
            <p className="text-xs text-muted-foreground">Earned: £{earned.toFixed(2)}</p>
          </div>
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="py-2">Creator</th>
                <th className="py-2">Date</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Bonus</th>
              </tr>
            </thead>
            <tbody>
              {meta.data!.referrals.map((r: any) => {
                const refHandle = Array.isArray(r.referred) ? r.referred[0]?.handle : r.referred?.handle;
                const refStatus = Array.isArray(r.referred) ? r.referred[0]?.status : r.referred?.status;
                const approved = refStatus === "approved" || refStatus === "live";
                return (
                  <tr key={r.id} className="border-t border-border">
                    <td className="py-2">{approved && refHandle ? `@${refHandle}` : "Pending"}</td>
                    <td className="py-2 text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="py-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${r.status === "paid" ? "bg-emerald-100 text-emerald-900" : r.status === "cancelled" ? "bg-rose-100 text-rose-900" : "bg-amber-100 text-amber-900"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums">£{Number(r.bonus_gbp).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
